import {
  injectJsonLd,
  removeJsonLd,
  setMetaTags,
  type PageMetaOptions,
} from './meta';
import { absoluteUrl, forumCanonical, setBreadcrumbJsonLd, type BreadcrumbItem } from './seo';
import { createCategorySlug } from './urlHelpers';
import { calculateForumScore } from './forumScore';
import { getForumRank } from './forumRank';
import type { ForumBase } from '../types/forum';

export type ForumSeoInput = {
  forum_id: string;
  name: string;
  category: string;
  url?: string | null;
  description?: string | null;
  logo_url?: string | null;
  members_count: number;
  topics_count: number;
  posts_count: number;
  founded_year?: number;
  software?: string | null;
  updated_at?: string | null;
};

/** Marka araması için kısa title: "ForumAdı | kitapcik.net" */
export function getForumPageTitle(forumName: string): string {
  const name = forumName.trim() || 'Forum';
  return `${name} | kitapcik.net`;
}

export function getForumMetaDescription(
  forum: ForumSeoInput,
  opts?: { globalRank?: number; categoryRank?: number }
): string {
  const members = forum.members_count.toLocaleString('tr-TR');
  const topics = forum.topics_count.toLocaleString('tr-TR');
  const posts = forum.posts_count.toLocaleString('tr-TR');
  const parts = [
    `${forum.name} — Türkiye forum rehberi ve güncel istatistik sayfası.`,
    `${forum.category} kategorisinde ${members} üye, ${topics} konu, ${posts} ileti.`,
  ];
  if (opts?.globalRank != null && opts.globalRank > 0) {
    parts.push(`Genel sıralama #${opts.globalRank}.`);
  }
  if (opts?.categoryRank != null && opts.categoryRank > 0) {
    parts.push(`Kategoride #${opts.categoryRank}.`);
  }
  parts.push('kitapcik.net');
  return parts.join(' ');
}

export function getForumKeywords(forum: ForumSeoInput): string {
  const name = forum.name.trim();
  const lower = name.toLowerCase();
  const id = forum.forum_id.trim().toLowerCase();
  const cat = forum.category.trim().toLowerCase();
  const tokens = new Set<string>([
    name,
    lower,
    id,
    `${lower} forum`,
    `${name} forum`,
    `${name} istatistikleri`,
    `${lower} istatistikleri`,
    `${name} üye sayısı`,
    `${lower} üye`,
    cat,
    `${cat} forumu`,
    'kitapcik',
    'kitapcik.net',
    'türk forumları',
    'forum istatistikleri',
    'forum rehberi',
  ]);
  return [...tokens].join(', ');
}

function forumBreadcrumbItems(forum: ForumSeoInput): BreadcrumbItem[] {
  return [
    { name: 'Ana Sayfa', path: '/' },
    { name: 'Kategoriler', path: '/category' },
    {
      name: forum.category,
      path: `/category/${createCategorySlug(forum.category)}`,
    },
    { name: forum.name, path: `/forum/${forum.forum_id}` },
  ];
}

function buildForumWebPageJsonLd(
  forum: ForumSeoInput,
  description: string
): Record<string, unknown> {
  const pageUrl = forumCanonical(forum.forum_id);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': pageUrl,
    url: pageUrl,
    name: forum.name,
    description,
    inLanguage: 'tr-TR',
    isPartOf: {
      '@type': 'WebSite',
      name: 'kitapcik.net',
      alternateName: 'Türk Forum İstatistikleri',
      url: absoluteUrl('/'),
    },
    about: {
      '@type': 'Organization',
      name: forum.name,
      url: forum.url || pageUrl,
      ...(forum.logo_url ? { logo: forum.logo_url } : {}),
    },
    mainEntity: {
      '@type': 'WebSite',
      name: forum.name,
      url: forum.url || pageUrl,
      interactionStatistic: [
        {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/JoinAction',
          userInteractionCount: forum.members_count,
        },
        {
          '@type': 'InteractionCounter',
          interactionType: 'https://schema.org/CommentAction',
          userInteractionCount: forum.posts_count,
        },
      ],
    },
  };
}

function buildForumFaqJsonLd(forum: ForumSeoInput): Record<string, unknown> {
  const members = forum.members_count.toLocaleString('tr-TR');
  const topics = forum.topics_count.toLocaleString('tr-TR');
  const posts = forum.posts_count.toLocaleString('tr-TR');
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${forum.name} nedir?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${forum.name}, ${forum.category} kategorisinde listelenen Türkiye forum topluluğudur. Güncel üye ve ileti verileri kitapcik.net üzerinde yayınlanır.`,
        },
      },
      {
        '@type': 'Question',
        name: `${forum.name} kaç üye?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${forum.name} forumunda kitapcik.net verilerine göre ${members} kayıtlı üye bulunuyor.`,
        },
      },
      {
        '@type': 'Question',
        name: `${forum.name} forum istatistikleri`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${forum.name}: ${members} üye, ${topics} konu, ${posts} ileti. Detaylı sıralama ve kategori kıyası için kitapcik.net/forum/${forum.forum_id} sayfasını ziyaret edin.`,
        },
      },
    ],
  };
}

export function computeForumRanks(
  forum: ForumSeoInput,
  allForums: ForumBase[]
): { globalRank?: number; categoryRank?: number } {
  if (allForums.length < 2) return {};
  const base = forum as ForumBase;
  return {
    globalRank: getForumRank(base, allForums, 'score'),
    categoryRank: getForumRank(
      base,
      allForums.filter((f) => f.category === forum.category),
      'score'
    ),
  };
}

/** Forum detay sayfası — meta, canonical, OG, JSON-LD, breadcrumb */
export function applyForumPageSeo(forum: ForumSeoInput, allForums: ForumBase[] = []): void {
  const ranks = computeForumRanks(forum, allForums);
  const description = getForumMetaDescription(forum, ranks);
  const keywords = getForumKeywords(forum);

  const meta: PageMetaOptions = {
    title: getForumPageTitle(forum.name),
    description,
    keywords,
    ogImage: forum.logo_url || `${absoluteUrl('/')}/og-image.png`,
    canonical: forumCanonical(forum.forum_id),
    ogType: 'article',
    titleExact: true,
    siteName: 'kitapcik.net',
  };

  setMetaTags(meta);
  setBreadcrumbJsonLd(forumBreadcrumbItems(forum));
  injectJsonLd(buildForumWebPageJsonLd(forum, description), 'json-ld-page');
  injectJsonLd(buildForumFaqJsonLd(forum), 'json-ld-faq');
}

export function forumScoreForSeo(forum: ForumSeoInput): number {
  return calculateForumScore(
    forum.topics_count,
    forum.posts_count,
    forum.members_count,
    forum.founded_year ?? 1
  );
}
